/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC } from "react"
import { Dropdown, Button, Menu, Row, Col } from "@duxton-web/ui"
import {
  AddCircleFilled,
  ChevronDownUniversal,
  EditableFilled,
} from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { isNil } from "lodash"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { useTranslation } from "common/utils/i18n"
import { Section } from "types/models/menu"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { OverflowTip } from "components/common"
import {
  dropdownMenuItemStyle,
  dropdownTextStyle,
  dropdownButton,
  dropdownEditButton,
} from "./dropdownStyles"
import { SectionPeriods } from "./SectionPeriods"

interface SectionDropdownProps {
  disabled?: boolean
}

interface SectionRowProps {
  section: Section
}

export const SectionRow: FC<SectionRowProps> = ({
  section,
}: SectionRowProps) => {
  const selectedSection = useSelector(
    (state: AppState) => state.menu.overview.section,
  )

  const {
    merchantID,
    menu: { sections },
    isEditAllowed,
    isMartDepartmentStructure,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const dispatch = useDispatch()

  const handleClickSelectSection = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.selectSection,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SECTION_ID: section.sectionID,
        PREVIOUS_SECTION_ID: selectedSection?.sectionID ?? null,
      },
    })
    dispatch(actionCreator.menu.selectSection(section))
  }
  const handleClickEditSection = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickEditSection,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SECTION_ID: section.sectionID,
        PREVIOUS_SECTION_ID: selectedSection?.sectionID ?? null,
      },
    })
    dispatch(
      actionCreator.menu.toggleDrawer({
        section,
        showSection: true,
      }),
    )
  }

  return (
    <Row
      align="middle"
      justify="space-between"
      gutter={16}
      onClick={(e: any) => {
        // small hack to prevent selecting section
        const isClickingEditBtn = !isNil(e?.target?.closest(".btnEdit"))
        if (!isClickingEditBtn) {
          handleClickSelectSection()
        }
      }}
    >
      <Col>
        <span css={dropdownTextStyle}>{section.sectionName}</span>
        {sections && sections.length > 1 && (
          <SectionPeriods section={section} />
        )}
      </Col>
      {isEditAllowed && !isMartDepartmentStructure && (
        <Col>
          <Button
            className="btnEdit"
            css={dropdownEditButton}
            size="small"
            shape="circle"
            onClick={handleClickEditSection}
          >
            <EditableFilled />
          </Button>
        </Col>
      )}
    </Row>
  )
}

export const AddSectionMenu: FC = () => {
  const selectedSection = useSelector(
    (state: AppState) => state.menu.overview.section,
  )

  const { merchantID } = useSelector((state: AppState) => state.menu)

  const addNewSectionTranslation = useTranslation(
    "menu.overview.buttons.addNewSection",
  )

  const dispatch = useDispatch()

  const handleClickAdd = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickAddSection,
      labels: {
        STORE_ID: merchantID,
        SECTION_ID: null,
        PREVIOUS_SECTION_ID: selectedSection?.sectionID ?? null,
      },
    })
    dispatch(
      actionCreator.menu.toggleDrawer({
        section: null,
        showSection: true,
      }),
    )
  }

  return (
    <div
      onClick={handleClickAdd}
      css={css`
        padding: 21px 0 !important;
      `}
    >
      <span
        css={css`
          font-size: 16px;
        `}
      >
        <AddCircleFilled
          css={css`
            font-size: 20px;
            color: #00a5cf;
            margin-right: 7px;
          `}
        />
        {addNewSectionTranslation}
      </span>
    </div>
  )
}

export const SectionDropdown: FC<SectionDropdownProps> = ({
  disabled,
}: SectionDropdownProps) => {
  const selectedSection = useSelector(
    (state: AppState) => state.menu.overview.section,
  )

  const {
    menu: { sections },
    isEditAllowed,
    isMartDepartmentStructure,
  } = useSelector((state: AppState) => state.menu)

  return (
    <Dropdown
      disabled={disabled}
      trigger={["click"]}
      overlay={
        <Menu>
          {sections?.map(section => (
            <Menu.Item
              key={section.sectionID}
              css={css`
                ${dropdownMenuItemStyle}
                &:not(:hover) {
                  .btnEdit {
                    visibility: hidden;
                  }
                }
              `}
            >
              <SectionRow section={section} />
            </Menu.Item>
          ))}
          {isEditAllowed && !isMartDepartmentStructure && (
            <Menu.Item
              key="add"
              css={css`
                text-align: center;
                padding: 0 !important;
              `}
            >
              <AddSectionMenu />
            </Menu.Item>
          )}
        </Menu>
      }
    >
      <Button
        css={css`
          ${dropdownButton}
          min-width: 0;
        `}
        shape="default"
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            align-items: end;
          `}
        >
          <OverflowTip>
            {selectedSection?.sectionName ?? (
              <span>
                {useTranslation("menu.overview.buttons.addNewSection")}
              </span>
            )}
          </OverflowTip>
          <span
            css={css`
              flex: 1 1 0px;
            `}
          />
          <ChevronDownUniversal />
        </div>
      </Button>
    </Dropdown>
  )
}
